/**
 * Stone Investment Page Hero
 * 
 * Editorial hero section for internal pages with Stone Investment's luxury aesthetic:
 * - Full-width image with gradient overlay
 * - Elegant typography
 * - Grain texture effect
 */

'use client';

import Image from 'next/image';
import type { PageHeroProps } from '@routeroyal/theme-core';

/**
 * StonePageHero - Editorial hero for internal pages
 */
export function StonePageHero({
  title,
  subtitle,
  backgroundImage,
  overlayOpacity = 0.5,
  size = 'medium'
}: PageHeroProps) {
  const sizeClasses = {
    small: 'py-24 lg:py-28',
    medium: 'py-32 lg:py-40',
    large: 'py-40 lg:py-56'
  };
  
  return (
    <section className={`relative ${sizeClasses[size]} overflow-hidden`}>
      {/* Background */}
      {backgroundImage ? (
        <div className="absolute inset-0">
          <Image
            src={backgroundImage}
            alt=""
            fill
            className="object-cover"
            priority
            sizes="100vw"
          />
          {/* Gradient Overlay */}
          <div 
            className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/40 to-stone-900/20"
            style={{ opacity: overlayOpacity }}
          />
          
          {/* Grain Texture */}
          <div 
            className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
              backgroundSize: '200px 200px'
            }}
          />
        </div>
      ) : (
        <div className="absolute inset-0 bg-stone-100" />
      )}
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-4xl">
          <h1
            className={`font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mb-6 tracking-tight leading-[0.95] ${
              backgroundImage ? 'text-white' : 'text-stone-900'
            }`}
            style={{ 
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              textShadow: backgroundImage ? '0 4px 40px rgba(0,0,0,0.4)' : 'none'
            }}
          >
            {title}
          </h1>
          
          {subtitle && (
            <p
              className={`text-lg lg:text-xl leading-relaxed font-sans max-w-2xl ${
                backgroundImage ? 'text-stone-200' : 'text-stone-600'
              }`}
              style={{ 
                fontFamily: "'Montserrat', sans-serif",
                textShadow: backgroundImage ? '0 2px 20px rgba(0,0,0,0.3)' : 'none'
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
      
      {/* Bottom Border */}
      {!backgroundImage && (
        <div className="absolute bottom-0 left-0 right-0 h-px bg-stone-200" />
      )}
    </section>
  );
}

export default StonePageHero;
